import { useState } from "react";
import { Link } from "react-router-dom";
import Seo, { localBusinessSchema, breadcrumbSchema } from "../components/Seo";
import { Reveal, Overline, FrameReveal } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import { useProjects, useSettings } from "../lib/hooks";
import { CATEGORIES, REGIONS, categoryLabel } from "../lib/site";

export default function Projects() {
  const [category, setCategory] = useState("");
  const [region, setRegion] = useState("");
  const { data: projects, isLoading } = useProjects({ category, location: region });
  const { data: allProjects } = useProjects();
  const activeRegions = REGIONS.filter((r) => (allProjects || []).some((p) => p.region === r.value));
  const { data: settings } = useSettings();
  const sortedProjects = [...(projects || [])].sort(
    (a, b) => (a.order ?? 999) - (b.order ?? 999)
  );

  return (
    <div data-testid="projects-page">
      <Seo
        title="Our Projects | NOOR Design & Build"
        description="Real extensions, renovations and conversions designed and built by NOOR across Bristol, Bath and Somerset — photographed on completion."
        path="/projects"
        schema={{ "@context": "https://schema.org", "@graph": [localBusinessSchema(settings), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Projects", path: "/projects" }])] }}
      />
      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <Overline light>[ Projects ]</Overline>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 max-w-3xl leading-[1.05]">
            Work we are proud to put our name to.
          </h1>
          <p className="text-white/60 text-base md:text-lg leading-relaxed mt-8 max-w-2xl">
            Real projects designed and built by NOOR across Bristol, Bath, Somerset and the
            surrounding areas — photographed on completion. New case studies are added as projects finish.
          </p>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12 pb-10 border-b border-black/10" data-testid="project-filters">
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-graphite/50 mb-3">Project Type</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setCategory("")} data-testid="filter-category-all"
                  className={`px-4 py-2 text-xs font-medium tracking-wide border transition-colors duration-300 ${!category ? "bg-graphite text-white border-graphite" : "border-black/15 hover:border-graphite"}`}>
                  All
                </button>
                {CATEGORIES.map((c) => (
                  <button key={c.value} onClick={() => setCategory(c.value === category ? "" : c.value)} data-testid={`filter-category-${c.value}`}
                    className={`px-4 py-2 text-xs font-medium tracking-wide border transition-colors duration-300 ${category === c.value ? "bg-graphite text-white border-graphite" : "border-black/15 hover:border-graphite"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-graphite/50 mb-3">Location</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setRegion("")} data-testid="filter-location-all"
                  className={`px-4 py-2 text-xs font-medium tracking-wide border transition-colors duration-300 ${!region ? "bg-graphite text-white border-graphite" : "border-black/15 hover:border-graphite"}`}>
                  All
                </button>
                {activeRegions.map((r) => (
                  <button key={r.value} onClick={() => setRegion(r.value === region ? "" : r.value)} data-testid={`filter-location-${r.value}`}
                    className={`px-4 py-2 text-xs font-medium tracking-wide border transition-colors duration-300 ${region === r.value ? "bg-graphite text-white border-graphite" : "border-black/15 hover:border-graphite"}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {isLoading && <p className="text-graphite/50 text-sm py-16">Loading projects…</p>}
          {!isLoading && (projects || []).length === 0 && (
            <div className="border border-black/10 p-8 md:p-12 max-w-2xl my-16" data-testid="projects-empty">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-ember mb-4">Portfolio Update</p>
              <p className="text-graphite/60 leading-relaxed">
                We are preparing a brand-new portfolio of our completed projects with genuine photography.
                In the meantime, references from recent work are shared during your consultation —{" "}
                <Link to="/request-a-consultation" className="text-ember underline">request one here</Link>.
              </p>
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-12">
            {sortedProjects.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                <Link to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`} className="group block transition-transform duration-500 ease-out hover:-translate-y-1.5">
                  <div className="border border-black/10 p-2 transition-colors duration-500 group-hover:border-ember/60">
                    <div className="relative overflow-hidden">
                      <FrameReveal src={p.cover} alt={`${p.title} — ${p.subtitle}`} className="aspect-[4/3]" />
                      <div className="absolute inset-0 bg-graphite/0 group-hover:bg-graphite/25 max-md:bg-graphite/25 transition-colors duration-500 flex items-center justify-center">
                        <span data-testid={`view-project-overlay-${p.slug}`} className="font-mono text-[10px] tracking-[0.35em] uppercase text-white opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 max-md:opacity-100 max-md:translate-y-0 transition-all duration-500 border border-white/40 px-4 py-2">
                          View Project
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 flex items-start justify-between gap-4">
                    <div>
                      <h2 className="font-display font-medium text-xl group-hover:text-ember transition-colors duration-300">{p.title}</h2>
                      <p className="text-sm text-graphite/55 mt-1">{p.subtitle}</p>
                      {p.placeholder && (
                        <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-graphite/45 mt-2">Design concept — illustrative imagery</p>
                      )}
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-graphite/40 shrink-0 mt-1.5 text-right">
                      {(p.categories || []).slice(0, 2).map(categoryLabel).join(" · ")}
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CtaBand />
    </div>
  );
}
